import type { Storey } from '@shared/models/design/storey.model';

/**
 * Web-Mercator ground resolution at zoom 0 and latitude 0 (m/pixel).
 *
 * Mapbox uses the same constant; the per-tile resolution at zoom `z` and
 * latitude `lat` is `EARTH_CIRCUMFERENCE_M_PER_PX_AT_Z0 * cos(lat) / 2^z`.
 */
export const EARTH_CIRCUMFERENCE_M_PER_PX_AT_Z0 = 156543.034;

/**
 * Axis-aligned bbox of all storey shape vertices in meters relative to the
 * building location origin. `width` and `depth` are the spans on the x/y
 * axes respectively.
 */
export type BuildingFootprintBbox = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  depth: number;
};

/**
 * Aggregate bbox of every shape vertex across all storeys, in meters
 * relative to the building location. Returns `undefined` when there are no
 * vertices to measure (empty design).
 */
export const getBuildingFootprintBbox = (
  storeys: readonly Storey[],
): BuildingFootprintBbox | undefined => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const storey of storeys) {
    for (const shape of storey.shapes) {
      for (const [x, y] of shape.points) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return undefined;

  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    depth: maxY - minY,
  };
};

/**
 * Inputs for {@link computeBuildingFitZoom}.
 */
export type BuildingFitZoomInput = {
  /** Container width in CSS pixels. */
  containerWidth: number;
  /** Container height in CSS pixels. */
  containerHeight: number;
  /** Latitude of the building (degrees). */
  latitudeDegrees: number;
  /** Building footprint bbox in meters (see {@link getBuildingFootprintBbox}). */
  bbox: BuildingFootprintBbox;
  /**
   * Fraction of the container's smaller dimension the footprint should
   * occupy. Smaller values leave more breathing room around the building.
   * The default leaves enough margin for a pitched/rotated cover view.
   * @default 0.6
   */
  paddingFactor?: number;
};

/**
 * Mapbox zoom level that fits {@link BuildingFitZoomInput.bbox} inside the
 * container, accounting for latitude-dependent Mercator scaling.
 *
 * Uses the bbox's largest horizontal dimension as the constraint and fits it
 * to {@link BuildingFitZoomInput.paddingFactor} of the container's smaller
 * dimension. The math assumes a **top-down** view. For a pitched camera (e.g.
 * report cover at 75°), use a **lower** {@link BuildingFitZoomInput.paddingFactor}
 * so the footprint does not clip at the viewport edges.
 *
 * Ground resolution at zoom `z` is `156543.034 * cos(lat) / 2^z` meters per
 * pixel. Solving for `z` when the building must span `targetPixels`:
 * `z = log2(targetPixels * 156543.034 * cos(lat) / footprintMeters)`.
 */
export const computeBuildingFitZoom = ({
  containerWidth,
  containerHeight,
  latitudeDegrees,
  bbox,
  paddingFactor = 0.6,
}: BuildingFitZoomInput): number | undefined => {
  const footprintMeters = Math.max(bbox.width, bbox.depth);
  if (footprintMeters <= 0) return undefined;
  if (containerWidth <= 0 || containerHeight <= 0) return undefined;

  const minDim = Math.min(containerWidth, containerHeight);
  const targetPixels = minDim * paddingFactor;
  const cosLat = Math.cos((latitudeDegrees * Math.PI) / 180);
  if (cosLat <= 0) return undefined;

  return Math.log2(
    (targetPixels * EARTH_CIRCUMFERENCE_M_PER_PX_AT_Z0 * cosLat) /
      footprintMeters,
  );
};
